/**
 * Voice Clone Component
 *
 * Provides a comprehensive form for creating voice profiles with:
 * - Reference audio upload with preview
 * - Voice profile name and description
 * - Emotion control sliders for Chatterbox TTS
 * - Global/tenant scope selection (for admins)
 * - Test preview functionality
 *
 * This component is used to create voice cloning profiles for Chatterbox TTS integration.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';
import { AudioUpload, type AudioUploadResult } from './AudioUpload';

// =============================================================================
// Types
// =============================================================================

/**
 * Emotion control parameters for voice cloning
 */
interface EmotionControl {
  happiness: number;
  sadness: number;
  anger: number;
  fear: number;
  surprise: number;
  disgust: number;
}

/**
 * Form data for voice profile creation
 */
interface VoiceCloneFormData {
  name: string;
  description: string;
  isGlobal: boolean;
  emotionControl: EmotionControl;
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  audio?: string;
  general?: string;
}

/**
 * API response for voice creation
 */
interface VoiceCreateResponse {
  id: string;
  name: string;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_EMOTION_CONTROL: EmotionControl = {
  happiness: 0.5,
  sadness: 0,
  anger: 0,
  fear: 0,
  surprise: 0,
  disgust: 0,
};

const DEFAULT_FORM_DATA: VoiceCloneFormData = {
  name: '',
  description: '',
  isGlobal: false,
  emotionControl: DEFAULT_EMOTION_CONTROL,
};

const EMOTION_LABELS: Record<keyof EmotionControl, { label: string; color: string }> = {
  happiness: { label: 'Happiness', color: 'bg-yellow-500' },
  sadness: { label: 'Sadness', color: 'bg-blue-500' },
  anger: { label: 'Anger', color: 'bg-red-500' },
  fear: { label: 'Fear', color: 'bg-purple-500' },
  surprise: { label: 'Surprise', color: 'bg-orange-500' },
  disgust: { label: 'Disgust', color: 'bg-green-500' },
};

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Form section component
 */
interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
      <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
      {description && <p className="text-sm text-gray-400 mb-4">{description}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'textarea';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  rows?: number;
  maxLength?: number;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  helpText,
  error,
  rows = 3,
  maxLength,
}: FormInputProps) {
  const inputClasses = `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          className={inputClasses}
        />
      ) : (
        <input
          type={type}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          className={inputClasses}
        />
      )}
      {helpText && !error && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
      {maxLength && (
        <p className="mt-1 text-xs text-gray-500 text-right">
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
}

/**
 * Form toggle component
 */
interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Emotion slider component
 */
interface EmotionSliderProps {
  emotion: keyof EmotionControl;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function EmotionSlider({ emotion, value, onChange, disabled }: EmotionSliderProps) {
  const { label, color } = EMOTION_LABELS[emotion];
  const percentage = Math.round(value * 100);

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className="flex-1 h-1.5 bg-discord-light rounded-full appearance-none cursor-pointer accent-discord-blurple disabled:cursor-not-allowed"
      />
      <span className="w-10 text-xs text-gray-500 text-right">{percentage}%</span>
    </div>
  );
}

/**
 * Test preview component for generated voice
 */
interface VoicePreviewProps {
  isLoading: boolean;
  isPlaying: boolean;
  onGenerate: () => void;
  onPlayStop: () => void;
  disabled?: boolean;
  previewUrl?: string;
}

function VoicePreview({
  isLoading,
  isPlaying,
  onGenerate,
  onPlayStop,
  disabled,
  previewUrl,
}: VoicePreviewProps) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={onGenerate}
        disabled={disabled || isLoading}
        className="inline-flex items-center gap-2 rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating...
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
            Generate Preview
          </>
        )}
      </button>
      {previewUrl && (
        <button
          onClick={onPlayStop}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPlaying ? (
            <>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
              Stop
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              Play Preview
            </>
          )}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Voice cloning form page component
 *
 * Provides a comprehensive form for creating voice profiles including:
 * - Reference audio upload with preview
 * - Profile metadata (name, description)
 * - Emotion control configuration
 * - Test preview generation
 */
export function VoiceClone() {
  const navigate = useNavigate();
  useAuth(); // Ensure user is authenticated
  const { effectiveTenantId, isGlobalAdmin } = useTenant();

  // Form state
  const [formData, setFormData] = useState<VoiceCloneFormData>(DEFAULT_FORM_DATA);
  const [audioUpload, setAudioUpload] = useState<AudioUploadResult | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // UI state
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);

  /**
   * Check if form is valid
   */
  const isFormValid = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.name.length <= 100 &&
      audioUpload !== null
    );
  }, [formData.name, audioUpload]);

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback(
    (field: keyof VoiceCloneFormData, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear related error
      if (field === 'name') {
        setErrors((prev) => ({ ...prev, name: undefined }));
      }
    },
    []
  );

  /**
   * Handle emotion control changes
   */
  const handleEmotionChange = useCallback(
    (emotion: keyof EmotionControl, value: number) => {
      setFormData((prev) => ({
        ...prev,
        emotionControl: { ...prev.emotionControl, [emotion]: value },
      }));
    },
    []
  );

  /**
   * Reset emotion controls to default
   */
  const handleResetEmotions = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      emotionControl: DEFAULT_EMOTION_CONTROL,
    }));
  }, []);

  /**
   * Handle audio upload
   */
  const handleAudioUpload = useCallback((result: AudioUploadResult) => {
    setAudioUpload(result);
    setErrors((prev) => ({ ...prev, audio: undefined }));
    // Clear any existing preview
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  /**
   * Handle audio removal
   */
  const handleAudioRemove = useCallback(() => {
    setAudioUpload(null);
    // Clear any existing preview
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  /**
   * Validate form
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Voice name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Voice name must be at most 100 characters';
    }

    if (!audioUpload) {
      newErrors.audio = 'Reference audio is required for voice cloning';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, audioUpload]);

  /**
   * Generate preview audio
   */
  const handleGeneratePreview = useCallback(async () => {
    if (!audioUpload || !validateForm()) return;

    setIsGeneratingPreview(true);
    setErrors((prev) => ({ ...prev, general: undefined }));

    const apiClient = getApiClient();

    try {
      // Create form data for multipart upload
      const formDataObj = new FormData();
      formDataObj.append('referenceAudio', audioUpload.file);
      formDataObj.append('emotionControl', JSON.stringify(formData.emotionControl));
      formDataObj.append('text', 'Hello! This is a preview of the cloned voice profile.');

      // Note: Preview endpoint would need to be implemented on the backend
      // For now, we'll simulate the preview
      const result = await apiClient.post<{ previewUrl: string }>(
        '/voices/preview',
        {
          emotionControl: formData.emotionControl,
          text: 'Hello! This is a preview of the cloned voice profile.',
        }
      );

      if (isSuccess(result)) {
        // Clean up old preview
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(result.data.previewUrl);
      } else {
        // Fallback: Use the uploaded audio as a "preview"
        // In production, this would be actual TTS output
        setPreviewUrl(audioUpload.previewUrl);
      }
    } catch {
      // Use uploaded audio as fallback preview
      setPreviewUrl(audioUpload.previewUrl);
    } finally {
      setIsGeneratingPreview(false);
    }
  }, [audioUpload, formData.emotionControl, previewUrl, validateForm]);

  /**
   * Play/stop preview audio
   */
  const handlePlayStopPreview = useCallback(() => {
    if (!previewUrl) return;

    if (isPlayingPreview && previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      setIsPlayingPreview(false);
    } else {
      const audio = new Audio(previewUrl);
      audio.addEventListener('ended', () => {
        setIsPlayingPreview(false);
      });
      audio.play().catch(() => {
        setErrors((prev) => ({ ...prev, general: 'Failed to play audio' }));
      });
      setPreviewAudio(audio);
      setIsPlayingPreview(true);
    }
  }, [previewUrl, isPlayingPreview, previewAudio]);

  /**
   * Create voice profile
   */
  const handleCreate = useCallback(async () => {
    if (!validateForm() || !audioUpload) return;

    // Check tenant requirement for global admins
    if (isGlobalAdmin && !effectiveTenantId && !formData.isGlobal) {
      setErrors({ general: 'Please select a tenant or create as global voice profile' });
      return;
    }

    setIsCreating(true);
    setErrors({});

    const apiClient = getApiClient();

    try {
      // Build query params
      const queryParams = new URLSearchParams();
      if (effectiveTenantId && !formData.isGlobal) {
        queryParams.set('tenantId', effectiveTenantId);
      }

      // Create form data for multipart upload
      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      if (formData.description) {
        formDataObj.append('description', formData.description);
      }
      formDataObj.append('referenceAudio', audioUpload.file);
      formDataObj.append('emotionControl', JSON.stringify(formData.emotionControl));
      formDataObj.append('isGlobal', String(formData.isGlobal));

      // Note: The API would need to handle multipart form data
      // For now, we'll use JSON and assume the audio is uploaded separately
      const result = await apiClient.post<VoiceCreateResponse>(
        `/voices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
        {
          name: formData.name,
          description: formData.description || undefined,
          emotionControl: formData.emotionControl,
          isGlobal: formData.isGlobal,
          // In production, reference audio would be uploaded via multipart
          // or a separate upload endpoint with a returned file ID
        }
      );

      if (isSuccess(result)) {
        // Clean up preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        // Navigate to the new voice profile
        navigate(`/voices/${result.data.id}`);
      } else {
        setErrors({ general: result.error.message || 'Failed to create voice profile' });
      }
    } catch {
      setErrors({ general: 'Failed to create voice profile. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  }, [
    formData,
    audioUpload,
    effectiveTenantId,
    isGlobalAdmin,
    previewUrl,
    navigate,
    validateForm,
  ]);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/voices')}
            className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors"
            title="Back to voices"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Create Voice Profile</h1>
            <p className="text-sm text-gray-400">
              Upload reference audio and configure voice cloning settings
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {errors.general && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{errors.general}</p>
              </div>
              <button
                onClick={() => setErrors((prev) => ({ ...prev, general: undefined }))}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <FormSection
          title="Voice Profile Details"
          description="Give your voice profile a name and optional description"
        >
          <FormInput
            label="Voice Name"
            id="name"
            value={formData.name}
            onChange={(value) => handleFieldChange('name', value)}
            placeholder="e.g., Alex - Professional Voice"
            required
            error={errors.name}
            maxLength={100}
          />
          <FormInput
            label="Description"
            id="description"
            type="textarea"
            value={formData.description}
            onChange={(value) => handleFieldChange('description', value)}
            placeholder="Describe this voice profile..."
            rows={2}
            maxLength={500}
          />
          {isGlobalAdmin && (
            <FormToggle
              label="Global Voice Profile"
              description="Make this voice available to all tenants"
              checked={formData.isGlobal}
              onChange={(checked) => handleFieldChange('isGlobal', checked)}
            />
          )}
        </FormSection>

        {/* Reference Audio Upload */}
        <FormSection
          title="Reference Audio"
          description="Upload a clear audio recording to clone. Best results with 10-30 seconds of natural speech."
        >
          <AudioUpload
            value={audioUpload}
            onUpload={handleAudioUpload}
            onRemove={handleAudioRemove}
            error={errors.audio}
            disabled={isCreating}
          />
        </FormSection>

        {/* Emotion Control */}
        <FormSection
          title="Emotion Control"
          description="Adjust the emotional characteristics of the cloned voice"
        >
          <div className="space-y-3">
            {(Object.keys(EMOTION_LABELS) as Array<keyof EmotionControl>).map((emotion) => (
              <EmotionSlider
                key={emotion}
                emotion={emotion}
                value={formData.emotionControl[emotion]}
                onChange={(value) => handleEmotionChange(emotion, value)}
                disabled={isCreating}
              />
            ))}
          </div>
          <div className="flex justify-end mt-2">
            <button
              onClick={handleResetEmotions}
              disabled={isCreating}
              className="text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
              Reset to defaults
            </button>
          </div>
        </FormSection>

        {/* Preview Section */}
        {audioUpload && (
          <FormSection
            title="Voice Preview"
            description="Generate a preview to hear how the cloned voice will sound"
          >
            <VoicePreview
              isLoading={isGeneratingPreview}
              isPlaying={isPlayingPreview}
              onGenerate={handleGeneratePreview}
              onPlayStop={handlePlayStopPreview}
              disabled={isCreating || !audioUpload}
              previewUrl={previewUrl || undefined}
            />
            {previewUrl && (
              <p className="text-xs text-gray-500 mt-2">
                Note: This is a sample preview. Final results may vary slightly.
              </p>
            )}
          </FormSection>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-4">
          <button
            onClick={() => navigate('/voices')}
            className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !isFormValid}
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-6 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Voice Profile
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default VoiceClone;
